import { useSyncExternalStore } from 'react';

/**
 * A boolean CSS media query, live across resize/rotation.
 *
 * `useSyncExternalStore` over `matchMedia`'s own `change` event rather than a
 * `useState` + `useEffect` resize listener: `matchMedia` already fires only
 * when the query's truth value flips, so there is nothing to debounce and
 * nothing to compute on every pixel of a drag.
 *
 * **This is how the file browser's card/table swap decides which one to
 * mount, not which one to hide.** `apps/web/vitest.config.ts` sets `css:
 * false`, so a `hidden lg:block` pair would leave both views in the DOM in
 * every test — this hook makes the swap a real unmount instead, which is
 * also what a screen reader and the tab order want in a real browser: no
 * duplicate control sitting behind `display: none`.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mediaQueryList = window.matchMedia(query);
      mediaQueryList.addEventListener('change', onChange);
      return () => {
        mediaQueryList.removeEventListener('change', onChange);
      };
    },
    () => window.matchMedia(query).matches,
  );
}
